import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ComputerIcon from '@mui/icons-material/Computer';
import BarChartIcon from '@mui/icons-material/BarChart';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import PsychologyIcon from '@mui/icons-material/Psychology';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';

const HeroSection: React.FC = () => {
  const theme = useTheme();

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          right: '-10%',
          width: '40%',
          height: '40%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.primary.main}20 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          left: '-10%',
          width: '35%',
          height: '35%',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${theme.palette.secondary.main}20 0%, transparent 70%)`,
          filter: 'blur(40px)',
        }}
      />

      <Container maxWidth="lg">
        <Grid container spacing={4} alignItems="center">
          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography
                component="h1"
                variant="h2"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  lineHeight: 1.2,
                }}
              >
                Solutions Informatiques{' '}
                <Box
                  component="span"
                  sx={{
                    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Innovantes
                </Box>
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  mb: 4,
                  color: 'text.secondary',
                  fontWeight: 400,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                }}
              >
                Conseil, Développement et Data Science pour transformer votre entreprise
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => scrollToSection('services')}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: '1.1rem',
                  }}
                >
                  Découvrir nos services
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => scrollToSection('contact')}
                  sx={{
                    py: 1.5,
                    px: 3,
                    fontSize: '1.1rem',
                  }}
                >
                  Nous contacter
                </Button>
              </Box>

              <Grid container spacing={3} sx={{ mt: 4 }}>
                <Grid size={{ xs: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      7+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Domaines d'expertise
                    </Typography>
                  </motion.div>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4, duration: 0.5 }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      10+
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Années d'expérience
                    </Typography>
                  </motion.div>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5, duration: 0.5 }}
                  >
                    <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      FR
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Basé en France
                    </Typography>
                  </motion.div>
                </Grid>
              </Grid>
            </motion.div>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Box
                sx={{
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <svg
                  width="100%"
                  height="auto"
                  viewBox="0 0 500 500"
                  style={{ maxWidth: '500px' }}
                >
                  <defs>
                    <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: theme.palette.primary.main, stopOpacity: 1 }} />
                      <stop offset="100%" style={{ stopColor: theme.palette.secondary.main, stopOpacity: 1 }} />
                    </linearGradient>
                  </defs>
                  
                  <motion.circle
                    cx="250"
                    cy="250"
                    r="200"
                    fill="none"
                    stroke="url(#grad1)"
                    strokeWidth="2"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, ease: "easeInOut" }}
                  />
                  
                  <motion.circle
                    cx="250"
                    cy="250"
                    r="150"
                    fill="none"
                    stroke="url(#grad1)"
                    strokeWidth="1"
                    opacity="0.5"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 2, delay: 0.5, ease: "easeInOut" }}
                  />
                  
                  <motion.path
                    d="M250,150 L300,250 L250,350 L200,250 Z"
                    fill="url(#grad1)"
                    opacity="0.2"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1, delay: 1 }}
                    style={{ transformOrigin: 'center' }}
                  />
                  
                  <motion.text
                    x="250"
                    y="250"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fill="url(#grad1)"
                    fontSize="60"
                    fontWeight="bold"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.5 }}
                  >
                    EZ
                  </motion.text>
                </svg>

                <motion.div
                  animate={{
                    rotate: 360,
                  }}
                  transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "linear",
                  }}
                  style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    maxWidth: '500px',
                  }}
                >
                  {[0, 60, 120, 180, 240, 300].map((angle, index) => (
                    <Box
                      key={index}
                      sx={{
                        position: 'absolute',
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}40, ${theme.palette.secondary.main}40)`,
                        top: '50%',
                        left: '50%',
                        transform: `translate(-50%, -50%) rotate(${angle}deg) translateX(180px)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: theme.palette.primary.main,
                      }}
                    >
                      {index === 0 && <ComputerIcon />}
                      {index === 1 && <BarChartIcon />}
                      {index === 2 && <CloudIcon />}
                      {index === 3 && <SecurityIcon />}
                      {index === 4 && <PsychologyIcon />}
                      {index === 5 && <PhoneAndroidIcon />}
                    </Box>
                  ))}
                </motion.div>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default HeroSection;